import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Immutable from 'immutable';
import type {
  ImmutableRootState,
  ImmutableEntity,
} from '@uncinc/react-drupal-core/types';
import classnames from 'classnames';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  setOperationStatus,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
} from '@uncinc/react-drupal-core';
import { Button } from '@uncinc/uncinc-react-kitchen-sink';

import { createOrUpdateUserProfile } from '../../store/actions/Profile';
import InformationOverlay from '../InformationOverlay';
import { DialogConfirm } from '../DialogConfirm';
import { useDialogDrawerContext } from '../../context/DialogDrawerContext';
import AddressForm from '../AddressForm';
import AddressTeaser from './AddressTeaser';
import {
  getUsableAddresses,
  defaultIndexAfterDelete,
} from './getUsableAddresses';
import type { UserProfileAddress } from '../../types/user';
import { DEFAULT_COUNTRY_CODE } from '../../config/constants';

import './style.scss';

export interface AddressOverviewProps {
  selectionMode?: boolean;
  title?: string;
  activeIndex?: number;
  onSelect?: Function;
}

const AddressOverview: React.FC<AddressOverviewProps> = ({
  selectionMode = false,
  title,
  onSelect = () => {},
  activeIndex = null,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { closeDialog } = useDialogDrawerContext();
  // In selection mode the user stages a choice (pendingIndex) and applies it
  // via the confirm button, instead of selecting immediately on click. It is
  // seeded with the currently-active address so it is pre-highlighted on open.
  const [pendingIndex, setPendingIndex] = useState<number | null>(activeIndex);
  const uid = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], 0));
  const userProfile = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', uid, 'profile'], undefined));
  const userProfileAddresses = useSelector<
  ImmutableRootState<Immutable.List<ImmutableEntity<UserProfileAddress>>>,
  Immutable.List<ImmutableEntity<UserProfileAddress>>
  >((state) => state.getIn(['user', uid, 'profile', 'address'], Immutable.List()));
  const defaultAddressIndex = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(
      ['user', uid, 'profile', 'field_default_address_index', 0, 'value'],
      undefined,
    ),
  );
  const defaultBillingIndex = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(
      ['user', uid, 'profile', 'field_default_billing_index', 0, 'value'],
      undefined,
    ),
  );

  const {
    usableAddresses: usableProfileAddresses,
    toOriginalIndex: getOriginalIndex,
  } = getUsableAddresses(userProfileAddresses);

  // The default billing + delivery addresses sort to the top of the book
  // (design 333:2734). The original-index mapping is preserved so edit, delete
  // and set-default keep operating on the correct profile address.
  const isTopAddress = (usableIndex: number): boolean => {
    const original = getOriginalIndex(usableIndex);
    return (
      original === Number(defaultAddressIndex) ||
      original === Number(defaultBillingIndex)
    );
  };
  // Organisation name of a usable address, used as the alphabetical sort key.
  const organisationName = (usableIndex: number): string => String(usableProfileAddresses.getIn([usableIndex, 'organization']) || '');
  const orderedUsableIndices = usableProfileAddresses
    .toArray()
    .map((_address, i) => i)
    .sort((a, b) => {
      const topA = isTopAddress(a);
      const topB = isTopAddress(b);
      // Default billing + delivery addresses stay pinned to the top.
      if (topA !== topB) return topA ? -1 : 1;
      if (topA && topB) return 0;
      // The remaining addresses sort alphabetically by organisation name,
      // with addresses that have no organisation name last.
      const orgA = organisationName(a);
      const orgB = organisationName(b);
      if (Boolean(orgA) !== Boolean(orgB)) return orgA ? -1 : 1;
      return orgA.localeCompare(orgB, 'nl', { sensitivity: 'base' });
    });

  const profileUpdateStatus = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status'], ''),
  );

  const classNames = classnames('AddressOverview', {
    'AddressOverview--selection-mode': selectionMode,
  });

  // Address mutations operate on the RAW profile list at the original profile
  // index, so the stored default indices stay raw indices — matching every
  // reader (the overview reorder, the badges and the checkout pre-select all
  // index into the raw list). The empty placeholder address that the backend
  // creates is dropped for display only (see getUsableAddresses).

  // On address create.
  const onCreate = (address: any) => {
    const newIndex = userProfileAddresses.size;
    const data: any = {
      address: userProfileAddresses
        .insert(
          newIndex,
          Immutable.fromJS(address) as ImmutableEntity<UserProfileAddress>,
        )
        .toJS(),
    };
    if (address.set_default) {
      data.field_default_address_index = [{ value: String(newIndex) }];
    }
    if (address.set_default_billing) {
      data.field_default_billing_index = [{ value: String(newIndex) }];
    }
    dispatch(createOrUpdateUserProfile(data) as any);
  };

  // On address edit. Replacing in place does not move the other addresses, so
  // their defaults stay valid; only the edited address's own flags change.
  const onEdit = (originalIndex: number, address: any) => {
    const data: any = {
      address: userProfileAddresses
        .delete(originalIndex)
        .insert(
          originalIndex,
          Immutable.fromJS(address) as ImmutableEntity<UserProfileAddress>,
        )
        .toJS(),
    };
    if (address.set_default) {
      data.field_default_address_index = [{ value: String(originalIndex) }];
    } else if (originalIndex === Number(defaultAddressIndex)) {
      data.field_default_address_index = null;
    }
    if (address.set_default_billing) {
      data.field_default_billing_index = [{ value: String(originalIndex) }];
    } else if (originalIndex === Number(defaultBillingIndex)) {
      data.field_default_billing_index = null;
    }
    dispatch(createOrUpdateUserProfile(data) as any);
  };

  // On address destroy. Deleting shifts later addresses down by one, so a
  // default that pointed past the deleted entry is remapped (or cleared).
  const onDestroy = (originalIndex: number) => {
    const data: any = {
      address: userProfileAddresses.delete(originalIndex).toJS(),
    };
    const remap = (
      current: string | undefined,
    ): { value: string }[] | null | undefined => {
      if (current == null) return undefined;
      const next = defaultIndexAfterDelete(Number(current), originalIndex);
      return next === null ? null : [{ value: String(next) }];
    };
    const nextDelivery = remap(defaultAddressIndex);
    if (nextDelivery !== undefined) {
      data.field_default_address_index = nextDelivery;
    }
    const nextBilling = remap(defaultBillingIndex);
    if (nextBilling !== undefined) {
      data.field_default_billing_index = nextBilling;
    }
    // Addresses can't be empty.
    if (data.address.length === 0) {
      data.address = [
        {
          country_code: DEFAULT_COUNTRY_CODE,
        },
      ];
    }
    dispatch(createOrUpdateUserProfile(data) as any);
  };

  // Listens to the update status and shows a success or error toast message.
  useEffect(() => {
    if (profileUpdateStatus !== null) {
      if (profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS) {
        toast.success(t('mijn_vnl.overview.profile_success'));
      } else if (profileUpdateStatus === UI_OPERATION_STATUS_ERROR) {
        toast.error(t('mijn_vnl.overview.profile_error'));
      }
      dispatch(setOperationStatus('profile', 'update', null, null));
    }
  }, [profileUpdateStatus, dispatch, t]);

  // Activate a row. In selection mode this only stages the address (the user
  // applies it with the confirm button); otherwise the whole card is a
  // shortcut that opens that row's edit dialog (the My VNL overview).
  const handleRowActivate = (
    originalIndex: number,
    eventTarget: EventTarget | null,
  ) => {
    if (selectionMode) {
      setPendingIndex(originalIndex);
      return;
    }
    const item = (eventTarget as HTMLElement | null)?.closest(
      '.AddressOverview__address_item',
    );
    const editButton = item?.querySelector<HTMLElement>(
      '.AddressOverview__address_item__actions__editButton',
    );
    editButton?.click();
  };

  // Apply the staged selection and close the selector overlay.
  const confirmSelection = () => {
    if (pendingIndex !== null) {
      onSelect(pendingIndex);
    }
    closeDialog();
  };

  return (
    <div className={classNames}>
      {title && <h2>{title}</h2>}
      {userProfile && (
        <div>
          {usableProfileAddresses.size === 0 && (
            <p>{t('mijn_vnl.addresses.empty')}</p>
          )}
          <div className="AddressOverview__address_add_action">
            <InformationOverlay
              overlayKey="user_add_addresses"
              buttonIcon="plus"
              buttonTitle={t('address.add_new')}
              buttonClassName="Button--primary"
            >
              <AddressForm
                onSubmit={onCreate}
                allowSetDefault
                allowSetDefaultBilling
              />
            </InformationOverlay>
          </div>
          <div className="row">
            {orderedUsableIndices.map((index) => {
              const userProfileAddress = usableProfileAddresses.get(
                index,
              ) as ImmutableEntity<UserProfileAddress>;
              const originalIndex = getOriginalIndex(index);
              const addressOverviewItemClasses = classnames(
                'AddressOverview__address_item',
                {
                  selected: pendingIndex === originalIndex,
                },
              );

              return (
                <div className="col-12" key={`address-${originalIndex}`}>
                  <div className={addressOverviewItemClasses}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-pressed={pendingIndex === originalIndex}
                      className="Teaser"
                      onClick={(e) => handleRowActivate(originalIndex, e.target)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowActivate(originalIndex, e.target);
                        }
                      }}
                    >
                      {/* Render the Address Teaser. */}
                      <AddressTeaser
                        isDefault={
                          originalIndex === Number(defaultAddressIndex)
                        }
                        isDefaultBilling={
                          originalIndex === Number(defaultBillingIndex)
                        }
                        address={userProfileAddress}
                      />
                    </div>

                    {/* Address actions */}
                    <div className="AddressOverview__address_item__actions">
                      <div className="AddressOverview__address_item__actions__edit">
                        <InformationOverlay
                          overlayKey="user_edit_addresses"
                          buttonIcon="edit"
                          buttonTitle={t('address.change_address')}
                          buttonClassName="AddressOverview__address_item__actions__editButton"
                        >
                          <AddressForm
                            address={userProfileAddress.toJS()}
                            isDefault={
                              originalIndex === Number(defaultAddressIndex)
                            }
                            isDefaultBilling={
                              originalIndex === Number(defaultBillingIndex)
                            }
                            allowSetDefault
                            allowSetDefaultBilling
                            onSubmit={(data: any) => {
                              onEdit(originalIndex, data);
                            }}
                          />
                        </InformationOverlay>
                      </div>

                      {!selectionMode && (
                        <div className="AddressOverview__address_item__actions__delete">
                          <DialogConfirm
                            buttonText={t('address.delete')}
                            buttonIcon="trash"
                            onSubmit={() => {
                              onDestroy(originalIndex);
                            }}
                          >
                            <div className="AddressOverview__address_item__actions__delete__item">
                              <h3>{t('address.confirm_delete')}</h3>
                              <AddressTeaser address={userProfileAddress} />
                            </div>
                          </DialogConfirm>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {selectionMode && (
            <div className="AddressOverview__selection-actions">
              <Button className="Button--block" onClick={confirmSelection}>
                {t('general.confirm')}
              </Button>
              <Button
                className="Button Button--block Button--inverted"
                onClick={closeDialog}
              >
                {t('general.cancel')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressOverview;
